import { NAV_HIDDEN_MODULES, NAV_HIDDEN_TYPES } from "./hidden-modules";

/**
 * 사이드바 업무유형 선택 표시 판정 — 경로를 세그먼트 단위로 비교한다.
 *
 * `/w/trn` 은 `/w/trn_record`·`/w/trn_certify` 의 접두어라 단순 포함 검사로는 하위 메뉴가 함께 선택돼 보인다
 * (교육 관리·자격인증관리대장·개인별 교육 이력). 유형 경로 다음이 끝·`/`·`?`·`#` 일 때만 같은 유형으로 본다.
 */
export function isTypePathActive(pathname: string, typeCode: string): boolean {
  const seg = `/w/${typeCode.toLowerCase()}`;
  const at = pathname.indexOf(seg);
  if (at < 0) return false;
  const next = pathname.charAt(at + seg.length);
  return next === "" || next === "/" || next === "?" || next === "#";
}

/** 이름이 바뀐 업무유형 코드(옛 코드 → 현재 코드). 옛 주소·북마크·알림 링크 하위 호환용. */
const RENAMED_TYPE_CODES: Readonly<Record<string, string>> = {
  // 고객불만: Green 번호(COMP-YYYY-NNNN)에 맞춰 V170 에서 변경.
  CMP: "COMP",
  // 부적합: Green 번호(NCR-YYYY-NNNN)에 맞춰 V185 에서 변경.
  NC: "NCR",
};

/** 옛 업무유형 코드면 현재 코드를, 아니면 null. 대소문자 무관. */
export function currentTypeCode(code: string | undefined): string | null {
  if (!code) return null;
  return RENAMED_TYPE_CODES[code.toUpperCase() as keyof typeof RENAMED_TYPE_CODES] ?? null;
}

/**
 * 전용 화면이 `?item` 으로 드로어를 열지 않는 업무유형 — 연계 기록은 지금 화면에서 연다.
 * routes `WorkitemTypeRouter` 의 전용 화면 중 `?item` 을 읽지 않는 것(문서 기록·교육 이력·제품).
 */
const TYPES_WITHOUT_ITEM_DRAWER: ReadonlySet<string> = new Set([
  "DOC_RECORD",
  "TRN_RECORD",
  "PRODUCT",
]);

/**
 * 연계 기록을 열 경로 — 대상이 지금 화면과 다른 업무유형이고 그 유형 화면이 `?item` 드로어를 열 수 있으면
 * 그 유형 목록 + 드로어(`/:ws/w/:type?item=`). 같은 유형·유형 모름·전용 화면 미지원·사이드바 미노출 유형/모듈이면
 * null(호출자가 지금 화면의 `?item` 만 바꿈 — 범용 드로어로 열림).
 * 지금 화면 드로어에 다른 유형 기록(예: 고객불만 화면에 CAPA)이 뜨지 않게 한다.
 */
export function relationItemPath(
  workspace: string | undefined,
  currentTypeCode: string | undefined,
  types: ReadonlyArray<{ id: string; typeCode: string; moduleCode?: string | null }> | undefined,
  targetTypeId: string | undefined,
  targetItemId: string,
): string | null {
  const target = types?.find((t) => t.id === targetTypeId);
  const code = target?.typeCode;
  if (!workspace || !code || code === currentTypeCode) return null;
  if (
    TYPES_WITHOUT_ITEM_DRAWER.has(code) ||
    NAV_HIDDEN_TYPES.has(code) ||
    (target.moduleCode && NAV_HIDDEN_MODULES.has(target.moduleCode))
  )
    return null;
  return `/${workspace}/w/${code.toLowerCase()}?item=${encodeURIComponent(targetItemId)}`;
}
